import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Stack,
  Group,
  TextInput,
  Radio,
  Alert,
  Button,
  Text,
  Paper,
  ActionIcon,
  SegmentedControl,
  Box,
} from '@mantine/core';
import { IconAlertCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  AllergyIntolerance,
  Patient,
  Encounter,
  CodeableConcept,
  Coding,
  AllergyIntoleranceReaction,
} from '@medplum/fhirtypes';
import { CodeableConceptInput, CodingInput } from '@medplum/react';
import { notifications } from '@mantine/notifications';
import { DateInputWrapper } from '@/components/inputs/DateInputWrapper';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import { isEqual } from 'lodash';
import { ARKON, HL7 } from '@consts/systems.consts';
import {
  getAllergyCode,
  getCategory,
  getClinicalStatus,
  getOnsetDate,
  getReactions,
  getReactionManifestation,
  getReactionSeverity,
  setAllergyCode,
  setCategory,
  setClinicalStatus,
  setOnsetDate,
  addReaction,
  removeReaction,
  setReactionManifestation,
  setReactionSeverity,
  hasAllergyCode,
  hasClinicalStatus,
  createNewAllergyIntolerance,
} from '@/utils/allergy.utils';

export interface AllergyInputModalProps {
  opened: boolean;
  onClose: () => void;
  /** Function to save allergy to server. Returns the saved allergy with server-assigned ID. */
  onSave: (allergy: AllergyIntolerance) => Promise<AllergyIntolerance>;
  /** Callback called after successful save with the saved allergy. Used to update parent component state. */
  onSaveSuccess?: (allergy: AllergyIntolerance) => void;
  allergy?: AllergyIntolerance;
  mode: 'add' | 'edit';
  patient: Patient;
  encounter: Encounter;
}

// Default empty allergy for new entries
const createEmptyAllergy = (
  patient: Patient,
  encounter: Encounter
): AllergyIntolerance => createNewAllergyIntolerance(patient, encounter);

export const AllergyInputModal = ({
  opened,
  onClose,
  onSave,
  onSaveSuccess,
  allergy,
  mode,
  patient,
  encounter,
}: AllergyInputModalProps) => {
  const { t } = useTranslation(['patient-pages']);

  // Form state - using AllergyIntolerance directly
  const [formData, setFormData] = useState<AllergyIntolerance>(
    createEmptyAllergy(patient, encounter)
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Key to force CodeableConceptInput remount on each modal open
  const [inputKey, setInputKey] = useState(0);

  // Refs for FormActionButtons pattern
  const formActionsRef = useRef<FormActionButtonsRef>(null);
  const baselineRef = useRef<AllergyIntolerance | null>(null);
  const baselineDatesRef = useRef<{ onset: Date | null } | null>(null);
  const [baselineSet, setBaselineSet] = useState(false);

  // Date state (separate because FHIR uses strings, form uses Date)
  const [onsetDate, setOnsetDateState] = useState<Date | null>(null);

  // Reset form when modal opens or allergy changes
  useEffect(() => {
    if (opened) {
      // Increment key to force CodeableConceptInput to remount with fresh defaultValue
      setInputKey((prev) => prev + 1);
      setValidationError(null);
      setBaselineSet(true); // Flag to capture baseline after state is set
      if (allergy) {
        setFormData({ ...allergy });
        setOnsetDateState(getOnsetDate(allergy));
      } else {
        const empty = createEmptyAllergy(patient, encounter);
        setFormData(empty);
        setOnsetDateState(null);
      }
    } else {
      setBaselineSet(false);
    }
  }, [opened, allergy, patient, encounter]);

  // Capture baseline after form state is initialized
  useEffect(() => {
    if (opened && baselineSet) {
      baselineRef.current = { ...formData };
      baselineDatesRef.current = { onset: onsetDate };
      setBaselineSet(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, onsetDate]);

  // Update formData when date changes
  const updateDate = useCallback((date: Date | null) => {
    setFormData((prev) => setOnsetDate(prev, date));
  }, []);

  useEffect(() => {
    updateDate(onsetDate);
  }, [onsetDate, updateDate]);

  // Clear validation error on any change
  const clearValidation = () => {
    if (validationError) {
      setValidationError(null);
    }
  };

  // Change detection for FormActionButtons
  const hasUnsavedChanges = (): boolean => {
    if (!baselineRef.current) {
      return false;
    }

    try {
      const formChanged = !isEqual(formData, baselineRef.current);
      const datesChanged = !isEqual(
        { onset: onsetDate },
        baselineDatesRef.current
      );
      return formChanged || datesChanged;
    } catch {
      return true;
    }
  };

  // Discard handler for FormActionButtons
  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      setFormData({ ...baselineRef.current });
    }
    if (baselineDatesRef.current) {
      setOnsetDateState(baselineDatesRef.current.onset);
    }
    setInputKey((prev) => prev + 1); // Reset CodeableConceptInput
    setValidationError(null);
  };

  const handleSave = async () => {
    // Validate required fields
    if (!hasAllergyCode(formData)) {
      setValidationError(
        t('summaryPage.summary.allergies.dialog.substanceRequired', {
          defaultValue: 'Please select or enter a substance',
        })
      );
      return;
    }
    if (!hasClinicalStatus(formData)) {
      setValidationError(
        t('summaryPage.summary.allergies.dialog.clinicalStatusRequired', {
          defaultValue: 'Please select a clinical status',
        })
      );
      return;
    }

    // Save to server
    setSaving(true);
    try {
      const serverSaved = await onSave(formData);
      // Notify parent component of successful save
      if (onSaveSuccess) {
        onSaveSuccess(serverSaved);
      }
      onClose();
    } catch (error) {
      notifications.show({
        color: 'red',
        title: t('summaryPage.summary.allergies.dialog.saveErrorTitle', {
          defaultValue: 'Failed to save allergy',
        }),
        message: String(error),
      });
    } finally {
      setSaving(false);
    }
  };

  // Extract current values using getters
  const currentCode = getAllergyCode(formData);
  const currentCategory = getCategory(formData);
  const currentClinicalStatus = getClinicalStatus(formData);
  const reactions = getReactions(formData);

  // Get current clinical status code for SegmentedControl
  // Default to 'active' if no status is set
  const currentClinicalStatusCode =
    currentClinicalStatus?.coding?.[0]?.code || 'active';

  // Clinical status options for SegmentedControl
  const clinicalStatusOptions = [
    {
      value: HL7.TERMINOLOGY.CS.ALLERGY_INTOLERANCE.CLINICAL.CODES.ACTIVE,
      label: t(
        'summaryPage.summary.allergies.dialog.clinicalStatusOptions.active',
        {
          ns: 'patient-pages',
          defaultValue: 'Active',
        }
      ),
    },
    {
      value: HL7.TERMINOLOGY.CS.ALLERGY_INTOLERANCE.CLINICAL.CODES.INACTIVE,
      label: t(
        'summaryPage.summary.allergies.dialog.clinicalStatusOptions.inactive',
        {
          ns: 'patient-pages',
          defaultValue: 'Inactive',
        }
      ),
    },
    {
      value: HL7.TERMINOLOGY.CS.ALLERGY_INTOLERANCE.CLINICAL.CODES.RESOLVED,
      label: t(
        'summaryPage.summary.allergies.dialog.clinicalStatusOptions.resolved',
        {
          ns: 'patient-pages',
          defaultValue: 'Resolved',
        }
      ),
    },
  ];

  // Handle clinical status change from SegmentedControl
  const handleClinicalStatusChange = (value: string) => {
    const clinicalStatusCodeableConcept: CodeableConcept = {
      coding: [
        {
          system: HL7.TERMINOLOGY.CS.ALLERGY_INTOLERANCE.CLINICAL.BASE,
          code: value,
          display: t(
            `summaryPage.summary.allergies.dialog.clinicalStatusOptions.${value}`,
            {
              ns: 'patient-pages',
              defaultValue: value,
            }
          ),
        },
      ],
    };
    setFormData((prev) =>
      setClinicalStatus(prev, clinicalStatusCodeableConcept)
    );
    clearValidation();
  };

  // Add a new reaction
  const handleAddReaction = () => {
    // Create a new reaction with empty manifestation array (required by FHIR)
    const newReaction: AllergyIntoleranceReaction = {
      manifestation: [],
    };
    setFormData((prev) => addReaction(prev, newReaction));
    clearValidation();
  };

  // Remove a reaction
  const handleRemoveReaction = (index: number) => {
    setFormData((prev) => removeReaction(prev, index));
    clearValidation();
  };

  // Update reaction manifestation
  const handleReactionManifestationChange = (
    index: number,
    manifestation: CodeableConcept | undefined
  ) => {
    setFormData((prev) => setReactionManifestation(prev, index, manifestation));
    clearValidation();
  };

  // Update reaction severity
  const handleReactionSeverityChange = (
    index: number,
    severity: Coding | undefined
  ) => {
    setFormData((prev) => setReactionSeverity(prev, index, severity));
    clearValidation();
  };

  return (
    <Modal
      opened={opened}
      onClose={() => formActionsRef.current?.handleGuardedClose()}
      closeOnClickOutside={false}
      closeOnEscape={false}
      title={
        mode === 'add'
          ? t('summaryPage.summary.allergies.dialog.addTitle', {
              defaultValue: 'Add Allergy',
            })
          : t('summaryPage.summary.allergies.dialog.editTitle', {
              defaultValue: 'Edit Allergy',
            })
      }
      size="lg"
    >
      <Stack gap="md">
        {/* Validation Error */}
        {validationError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {validationError}
          </Alert>
        )}

        {/* Category */}
        <Radio.Group
          label={t('summaryPage.summary.allergies.dialog.category', {
            ns: 'patient-pages',
          })}
          value={currentCategory?.[0] || ''}
          onChange={(value) => {
            setFormData((prev) =>
              setCategory(prev, [value] as AllergyIntolerance['category'])
            );
            clearValidation();
          }}
        >
          <Group mt="xs" gap="md">
            <Radio
              value="medication"
              label={t(
                'summaryPage.summary.allergies.dialog.categoryOptions.medication',
                { ns: 'patient-pages' }
              )}
            />
            <Radio
              value="food"
              label={t(
                'summaryPage.summary.allergies.dialog.categoryOptions.food',
                { ns: 'patient-pages' }
              )}
            />
            <Radio
              value="environment"
              label={t(
                'summaryPage.summary.allergies.dialog.categoryOptions.environment',
                { ns: 'patient-pages' }
              )}
            />
            <Radio
              value="biologic"
              label={t(
                'summaryPage.summary.allergies.dialog.categoryOptions.biologic',
                { ns: 'patient-pages' }
              )}
            />
          </Group>
        </Radio.Group>

        {/* Substance/Code */}
        {currentCategory?.includes('medication') ? (
          <TextInput
            label={t('summaryPage.summary.allergies.dialog.substance', {
              ns: 'patient-pages',
            })}
            value={currentCode?.text || ''}
            onChange={(e) => {
              setFormData((prev) =>
                setAllergyCode(prev, { text: e.currentTarget.value })
              );
              clearValidation();
            }}
            required
          />
        ) : (
          <CodeableConceptInput
            key={`code-${inputKey}`}
            path="AllergyIntolerance.code"
            binding={ARKON.VS.ALLERGY_NO_DRUG_EL_EN}
            label={t('summaryPage.summary.allergies.dialog.substance', {
              ns: 'patient-pages',
            })}
            name="allergy-code"
            maxValues={1}
            defaultValue={currentCode}
            onChange={(code) => {
              setFormData((prev) => setAllergyCode(prev, code));
              clearValidation();
            }}
            outcome={undefined}
            required
          />
        )}

        {/* Reactions Section */}
        <Stack gap={2}>
          <Text fw={600} size="sm">
            {t('summaryPage.summary.allergies.dialog.reactions', {
              ns: 'patient-pages',
              defaultValue: 'Reactions',
            })}
          </Text>

          {reactions.length === 0 ? (
            <Paper withBorder radius="md" p="md" bg="gray.0">
              <Text size="sm" c="dimmed" ta="center">
                {t('summaryPage.summary.allergies.dialog.noReactions', {
                  ns: 'patient-pages',
                  defaultValue: 'No reactions added',
                })}
              </Text>
            </Paper>
          ) : (
            <Stack gap="md">
              {reactions.map((reaction, index) => {
                const manifestation = getReactionManifestation(reaction);
                const severity = getReactionSeverity(reaction);
                return (
                  <Paper key={index} p="md" withBorder radius="md" bg="gray.0">
                    <Stack gap="md">
                      {/* Manifestation */}
                      <CodeableConceptInput
                        key={`manifestation-${inputKey}-${index}-${manifestation?.coding?.[0]?.code || 'empty'}`}
                        path={`AllergyIntolerance.reaction[${index}].manifestation[0]`}
                        binding={ARKON.VS.ALLERGY_MANIFESTATION_EL_EN}
                        label={t(
                          'summaryPage.summary.allergies.dialog.manifestation',
                          {
                            ns: 'patient-pages',
                          }
                        )}
                        name={`manifestation-${index}`}
                        defaultValue={manifestation}
                        onChange={(manifestation) =>
                          handleReactionManifestationChange(
                            index,
                            manifestation
                          )
                        }
                        outcome={undefined}
                      />

                      <Group>
                        {/* Severity */}
                        <Box flex={1}>
                          <CodingInput
                            name={`severity-${index}`}
                            binding={ARKON.VS.ALLERGY_SEVERITY_EL_EN}
                            defaultValue={severity}
                            label={t(
                              'summaryPage.summary.allergies.dialog.severity',
                              {
                                ns: 'patient-pages',
                              }
                            )}
                            maxValues={1}
                            path={`AllergyIntolerance.reaction[${index}].severity`}
                            onChange={(severity) =>
                              handleReactionSeverityChange(index, severity)
                            }
                          />
                        </Box>
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          size="sm"
                          mt="lg"
                          onClick={() => handleRemoveReaction(index)}
                          title={t(
                            'summaryPage.summary.allergies.dialog.removeReaction',
                            {
                              ns: 'patient-pages',
                              defaultValue: 'Remove reaction',
                            }
                          )}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
          <Button
            leftSection={<IconPlus size={16} />}
            variant="light"
            size="xs"
            onClick={handleAddReaction}
            mt="xs"
          >
            {t('summaryPage.summary.allergies.dialog.addReaction', {
              ns: 'patient-pages',
              defaultValue: 'Add Reaction',
            })}
          </Button>
        </Stack>

        {/* Clinical Status */}
        <Stack gap={2}>
          <Text size="sm" fw={500}>
            {t('summaryPage.summary.allergies.dialog.clinicalStatus', {
              ns: 'patient-pages',
            })}
          </Text>
          <SegmentedControl
            fullWidth
            data={clinicalStatusOptions}
            value={currentClinicalStatusCode}
            onChange={handleClinicalStatusChange}
          />
        </Stack>

        {/* Onset Date */}
        <DateInputWrapper
          name="onsetDateTime"
          label={t('summaryPage.summary.allergies.dialog.onsetDate', {
            ns: 'patient-pages',
          })}
          value={onsetDate}
          onChange={(date) => {
            setOnsetDateState(date);
            clearValidation();
          }}
        />

        {/* Actions */}
        <FormActionButtons
          ref={formActionsRef}
          onSave={handleSave}
          onDiscard={handleDiscardChanges}
          onClose={onClose}
          hasUnsavedChanges={hasUnsavedChanges()}
          isLoading={saving}
          saveLabel={
            mode === 'add'
              ? t('summaryPage.summary.allergies.dialog.addButton', {
                  defaultValue: 'Add',
                })
              : t('summaryPage.summary.allergies.dialog.saveButton', {
                  defaultValue: 'Save',
                })
          }
        />
      </Stack>
    </Modal>
  );
};
